import React from 'react';
import type { Merge } from '../../types/utility';
import Button, { type ButtonPriority } from '../Button';
import ButtonRow from '../ButtonRow';
import Popover, { type PopoverProps } from './Popover';
import styles from './PopoverConfirm.module.scss';

export interface ConfirmProps {
  /** Style type for the Cancel <Button /> */
  cancelButtonPriority?: ButtonPriority;
  /** Label text for the Cancel <Button /> */
  cancelLabel?: string;
  /** The prompt message */
  prompt?: React.ReactNode;
  /** DOM class of the actual popover */
  className?: string;
  /** Style type for the Confirm <Button /> */
  confirmButtonPriority?: ButtonPriority;
  confirmButtonDestructive?: boolean;
  /** Label text for the Confirm <Button /> */
  confirmLabel?: string;
}

interface OwnProps extends ConfirmProps {
  /** This will be called when the Cancel button is clicked */
  onCancel?: () => void;
  /** This will be called when the Confirm button is clicked */
  onConfirm?: (event: React.SyntheticEvent) => void;
  targetCaret?: boolean;
}

type Props = Merge<PopoverProps, OwnProps>;

const PopoverConfirm: React.FC<Props> = props => {
  const {
    cancelButtonPriority,
    cancelLabel,
    prompt,
    confirmButtonPriority,
    confirmButtonDestructive,
    confirmLabel,
    onCancel,
    onConfirm,
    target,
    targetCaret,
    ...rest
  } = props;

  return (
    <Popover
      closeOnClick
      stopPropagation
      {...rest}
      targetCaret={targetCaret}
      target={target}
      className={styles.confirmModalContainer}
    >
      <div className={styles.content}>
        <p>{prompt}</p>
        <ButtonRow className={styles.buttons}>
          <Button
            data-testid="accept_popover_confirm"
            size="small"
            priority={confirmButtonPriority}
            onClick={onConfirm}
            destructive={confirmButtonDestructive}
          >
            {confirmLabel}
          </Button>
          <Button
            data-testid="cancel_popover_confirm"
            size="small"
            priority={cancelButtonPriority}
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
        </ButtonRow>
      </div>
    </Popover>
  );
};

PopoverConfirm.defaultProps = {
  cancelButtonPriority: 'medium',
  cancelLabel: 'Cancel',
  prompt: 'Are you sure you want to do this?',
  confirmButtonPriority: 'high',
  confirmLabel: 'Confirm',
  targetCaret: true,
  onCancel: () => {},
};

export default PopoverConfirm;
